import React, { useState, useEffect } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { Box, Grid, Typography, IconButton, Tooltip, Divider } from '@mui/material'
import { Settings } from '@mui/icons-material'
import { motion } from 'framer-motion'
import PortfolioBuilder from './components/PortfolioBuilder'
import Dashboard from './components/Dashboard'
import PortfolioCreator from './components/PortfolioCreator'
import LoadingScreen from './components/LoadingScreen'
import Data from './components/Data'
import darkTheme from './theme'

const App = () => {
  const [portfolio, setPortfolio] = useState([])
  const [result, setResult] = useState(null)
  const [availableAssets, setAvailableAssets] = useState([])
  const [loadingAssets, setLoadingAssets] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 9000)
    return () => clearTimeout(timer)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  }

  if (isLoading) {
    return <LoadingScreen onComplete={() => setIsLoading(false)} />
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}>
       
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 1.5
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            maxWidth: 1400,
            mx: 'auto',
            width: '100%'
          }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              YieldOptimizer
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Settings" arrow>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}>
                    <Settings />
                  </IconButton>
                </motion.div>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Data setAvailableAssets={(assets) => {
          setAvailableAssets(assets)
          setLoadingAssets(false)
        }} />
        
        
        <Box sx={{ 
          flex: 1,
          maxWidth: 1400,
          width: '100%',
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: 3
        }}>
          {loadingAssets ? (
            <Typography variant="h5" sx={{ color: 'text.primary', textAlign: 'center', mt: 5 }}>
              Loading assets... Please wait
            </Typography>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <Typography variant="subtitle1" sx={{ 
                color: 'text.secondary', 
                mb: 3,
                textAlign: 'center',
                maxWidth: 600,
                mx: 'auto'
              }}>
                Build a dividend portfolio with high-yield strategies
              </Typography>

              <Divider sx={{ my: 3, borderColor: 'divider' }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    position: 'sticky',
                    top: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                  }}>
                    <motion.div variants={itemVariants}>
                      <PortfolioCreator 
                        availableAssets={availableAssets} 
                        setPortfolio={setPortfolio} 
                      />
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                      <PortfolioBuilder
                        portfolio={portfolio}
                        setPortfolio={setPortfolio}
                        setResult={setResult}
                        availableAssets={availableAssets}
                        loadingAssets={loadingAssets}
                      />
                    </motion.div>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <motion.div variants={itemVariants}>
                    <Dashboard 
                      result={result} 
                      portfolio={portfolio} 
                      availableAssets={availableAssets} 
                    />
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default App